import React, { useEffect, useRef, useState } from 'react';
import styles from './FfrCaptureBar.module.scss';
import { ReactComponent as Close } from 'img/btn-close.svg';
import { ICON_MAP } from '@components/Icons/Icons';

interface FfrCaptureBarProps {
  onCancel?: () => void;
  thumbnails: CaptureThumb[];
  onRemoveThumbnail: (id: number) => void;
  onCapture?: () => void;
  onSubmit?: (options: { sendToPacs: boolean; download: boolean }) => void;
}

export interface CaptureThumb {
  id: number;
  url: string;
}

const FfrCaptureBar: React.FC<FfrCaptureBarProps> = ({
  onCancel,
  thumbnails,
  onRemoveThumbnail,
  onCapture,
  onSubmit,
}) => {
  const CheckboxIcon = ICON_MAP.checkbox;
  const CameraIcon = ICON_MAP.camera;
  const [sendToPacs, setSendToPacs] = useState(true);
  const [download, setDownload] = useState(false);
  const imgListViewportRef = useRef<HTMLDivElement>(null);
  const transparentPlaceholder =
    'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA=';

  const handleViewportWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const viewport = imgListViewportRef.current;
    if (!viewport) return;

    const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    if (delta === 0) return;

    event.preventDefault();
    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const nextTarget = Math.min(maxScrollLeft, Math.max(0, viewport.scrollLeft + delta));
    viewport.scrollTo({ left: nextTarget, behavior: 'smooth' });
  };

  useEffect(() => {
    const viewport = imgListViewportRef.current;
    if (viewport) {
      const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      viewport.scrollLeft = Math.min(viewport.scrollLeft, maxScrollLeft);
    }
  }, [thumbnails.length]);

  const submitDisabled = !sendToPacs && !download;

  return (
    <div className={styles.rootcanvas}>
      <div className={styles.screenshottraybar}>
        <div className={styles.thumbnailsarea}>
          <div className={styles.leftMain}>
            <div
              className={styles.imglistViewport}
              ref={imgListViewportRef}
              onWheel={handleViewportWheel}
            >
              <div className={styles.imglist}>
                {thumbnails.map((thumb) => (
                  <div className={styles.thumbContainer} key={thumb.id}>
                    <img
                      className={styles.captureimg}
                      src={thumb.url || transparentPlaceholder}
                      alt={`capture-${thumb.id}`}
                    />
                    <button
                      type="button"
                      className={styles.closeBtn}
                      onClick={() => onRemoveThumbnail(thumb.id)}
                      aria-label={`remove-capture-${thumb.id}`}
                    >
                      <Close />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              className={styles.captureButton}
              onClick={onCapture}
              disabled={thumbnails.length >= 10}
            >
              <CameraIcon className={styles.captureIcon} />
              <span>Capture</span>
            </button>
          </div>
          <div className={styles.divider} />
          <div className={styles.frame}>
            <span className={styles.currentText}>{thumbnails.length} Images</span>
            <span className={styles.totalText}>Max 10 images</span>
          </div>
        </div>

        <div className={styles.actionsarea}>
          <div className={styles.options}>
            <div className={styles.checkboxItem}>
              <div
                className={sendToPacs ? styles.boxPrimary : styles.box}
                onClick={() => setSendToPacs((prev) => !prev)}
                role="button"
                aria-label="toggle send to PACS"
              >
                {sendToPacs && <CheckboxIcon className={styles.checkIcon} />}
              </div>
              <span>Send to PACS</span>
            </div>
            <div className={styles.checkboxItem}>
              <div
                className={download ? styles.boxPrimary : styles.box}
                onClick={() => setDownload((prev) => !prev)}
                role="button"
                aria-label="toggle download"
              >
                {download && <CheckboxIcon className={styles.checkIcon} />}
              </div>
              <span>Download</span>
            </div>
          </div>
          <div className={styles.buttons}>
            <button onClick={onCancel}><span>Cancel</span></button>
            <button
              className={styles.primaryButton}
              disabled={submitDisabled}
              onClick={() => {
                if (submitDisabled) return;
                onSubmit?.({ sendToPacs, download });
              }}
            >
              <span>Submit</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FfrCaptureBar;
